import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiPost, apiPut } from '../utils/api';
import styles from './CreatePodcastPage.module.css';

const CreatePodcastPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Check if we're in edit mode
  const isEditMode = location.state?.editMode || false;
  const podcastId = location.state?.podcastId;
  const editFormData = location.state?.formData;
  
  const [formData, setFormData] = useState({
    title: editFormData?.title || '',
    prompt: editFormData?.prompt || '',
    frequency: 'none',
    duration: editFormData?.duration || '',
    hour_of_day: editFormData?.hour_of_day || '09',
    day_of_week: editFormData?.day_of_week || '',
    day_of_month: editFormData?.day_of_month || '',
    tone: editFormData?.tone || '',
    goals: editFormData?.goals || '',
    default_host: editFormData?.default_host || 'onyx',
    create_initial_episode: editFormData?.create_initial_episode || false,
    private: editFormData?.private || false
  });

  const [integrations, setIntegrations] = useState([]);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showIntegrationSelector, setShowIntegrationSelector] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationSettings, setIntegrationSettings] = useState({});
  const [editingIntegrationId, setEditingIntegrationId] = useState(null);

  // Load existing integrations when in edit mode
  useEffect(() => {
    const loadExistingIntegrations = async () => {
      if (isEditMode && podcastId && editFormData?.publications) {
        // If there are existing publications, create a search_web integration
        const existingIntegration = {
          id: Date.now(),
          type: 'search_web',
          settings: {
            publications: editFormData.publications
          }
        };
        setIntegrations([existingIntegration]);
      }
    };

    loadExistingIntegrations();
  }, [isEditMode, podcastId, editFormData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleAddIntegration = () => {
    setShowIntegrationSelector(true);
    document.body.style.overflow = 'hidden';
  };

  const handleSelectIntegration = (integrationType) => {
    setSelectedIntegration(integrationType);
    setIntegrationSettings({});
    setEditingIntegrationId(null);
    setShowIntegrationSelector(false);
    document.body.style.overflow = 'unset';
    setShowIntegrationModal(true);
  };

  const handleSaveIntegration = () => {
    if (selectedIntegration && integrationSettings) {
      if (editingIntegrationId) {
        // Update existing integration
        setIntegrations(prev => prev.map(integration => 
          integration.id === editingIntegrationId 
            ? { ...integration, settings: integrationSettings }
            : integration
        ));
      } else {
        // Create new integration
        const newIntegration = {
          id: Date.now(), // Simple ID generation
          type: selectedIntegration,
          settings: integrationSettings
        };
        setIntegrations(prev => [...prev, newIntegration]);
      }
    }
    
    setShowIntegrationModal(false);
    document.body.style.overflow = 'unset';
    setSelectedIntegration(null);
    setIntegrationSettings({});
    setEditingIntegrationId(null);
  };

  const handleEditIntegration = (integration) => {
    setSelectedIntegration(integration.type);
    setIntegrationSettings(integration.settings);
    setEditingIntegrationId(integration.id);
    document.body.style.overflow = 'hidden';
    setShowIntegrationModal(true);
  };

  const handleRemoveIntegration = (integrationId) => {
    setIntegrations(prev => prev.filter(i => i.id !== integrationId));
  };

  const handleIntegrationSettingsChange = (e) => {
    const { name, value } = e.target;
    setIntegrationSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const createInitialEpisode = async (podcastId) => {
    try {
      // Create episode
      const episodeResponse = await apiPost(`/podcast/${podcastId}/episodes/create-episode`, {
        title: 'Episode 1',
        notes: ''
      });

      if (!episodeResponse.ok) {
        throw new Error('Failed to create episode');
      }

      const episodeData = await episodeResponse.json();

      // Start script generation in background
      try {
        await apiPost(`/podcast/episode/${episodeData.id}/generate-script`);
      } catch (scriptErr) {
        console.warn('Script generation failed to start:', scriptErr);
        // Don't fail the whole process if script generation doesn't start
      }

      // Navigate to the podcast details page
      navigate(`/podcast/get-podcast/${podcastId}`);
    } catch (err) {
      console.error('Error creating initial episode:', err);
      // Still navigate to podcast page even if episode creation fails
      navigate(`/podcast/get-podcast/${podcastId}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Client-side validation based on frequency
      if (formData.frequency === 'weekly' && !formData.day_of_week) {
        setError('Day of week is required for weekly podcasts.');
        setLoading(false);
        return;
      }
      if (formData.frequency === 'monthly' && !formData.day_of_month) {
        setError('Day of month is required for monthly podcasts.');
        setLoading(false);
        return;
      }

      // Prepare data for API
      const podcastData = {
        title: formData.title,
        prompt: formData.prompt,
        frequency: formData.frequency,
        duration: formData.duration || null,
        hour_of_day: formData.hour_of_day,
        day_of_week: formData.day_of_week || null,
        day_of_month: formData.day_of_month || null,
        publications: integrations.find(i => i.type === 'search_web')?.settings?.publications || '',
        tone: formData.tone,
        goals: formData.goals,
        host: formData.default_host,
        create_initial_episode: formData.create_initial_episode,
        private: formData.private
      };
  
      let response;
      if (isEditMode) {
        response = await apiPut(`/podcast/update-podcast/${podcastId}`, podcastData);
      } else {
        response = await apiPost('/podcast/create-podcast', podcastData);
      }
      
      if (response.ok) {
        const data = await response.json();
        const newPodcastId = isEditMode ? podcastId : data.id;
        
        // If create_initial_episode is checked and we're creating (not editing)
        if (formData.create_initial_episode && !isEditMode) {
          // Create episode directly without showing modal
          await createInitialEpisode(newPodcastId);
        } else {
          // Redirect to the podcast's detail page
          navigate(`/podcast/get-podcast/${newPodcastId}`);
        }
      } else {
        const data = await response.json();
        setError(data.detail || `Failed to ${isEditMode ? 'update' : 'create'} podcast. Please try again.`);
      }
    } catch (err) {
      console.error('Create podcast error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className={styles.createPodcastPage}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>{isEditMode ? 'Edit Podcast' : 'Create New Podcast'}</h1>
          <p>{isEditMode ? 'Update your podcast configuration' : 'Set up your personalized podcast experience'}</p>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.titleSection}>
            <div className={styles.formGroup}>
              <label htmlFor="title">Podcast Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter your podcast title"
              />
            </div>
            <div className={styles.privateCheckbox}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="private"
                  checked={formData.private}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>Private</span>
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="prompt">Podcast Prompt *</label>
            <textarea
              id="prompt"
              name="prompt"
              value={formData.prompt}
              onChange={handleChange}
              required
              placeholder="Describe what you want your podcast to be about..."
              rows={4}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="duration">Duration *</label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              required
            >
              <option value="">Select duration</option>
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="auto">Auto (12-15 minutes)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="frequency">Frequency *</label>
            <select
              id="frequency"
              name="frequency"
              value="none"
              disabled
              className={styles.disabledSelect}
            >
              <option value="none">Ad-hoc</option>
            </select>
            <p className={styles.comingSoonText}>
              Daily, Weekly, and Monthly scheduling coming soon
            </p>
          </div>

          {/* Schedule Configuration - only show for scheduled frequencies */}
          {formData.frequency !== 'none' && (
            <div className={styles.scheduleSection}>
              <h3 className={styles.scheduleTitle}>Schedule Configuration</h3>
              <p className={styles.scheduleDescription}>
                Configure when your {formData.frequency} podcast should be generated
              </p>
              
              <div className={styles.scheduleFields}>
                <div className={styles.formGroup}>
                  <label htmlFor="hour_of_day">Hour of Day *</label>
                  <select
                    id="hour_of_day"
                    name="hour_of_day"
                    value={formData.hour_of_day}
                    onChange={handleChange}
                    required
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      const displayHour = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
                      return (
                        <option key={hour} value={hour}>
                          {displayHour}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {formData.frequency === 'weekly' && (
                  <div className={styles.formGroup}>
                    <label htmlFor="day_of_week">Day of Week *</label>
                    <select
                      id="day_of_week"
                      name="day_of_week"
                      value={formData.day_of_week}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select day</option>
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                )}

                {formData.frequency === 'monthly' && (
                  <div className={styles.formGroup}>
                    <label htmlFor="day_of_month">Day of Month *</label>
                    <select
                      id="day_of_month"
                      name="day_of_month"
                      value={formData.day_of_month}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select day</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}


          <div className={styles.formGroup}>
            <label htmlFor="tone">Tone *</label>
            <select
              id="tone"
              name="tone"
              value={formData.tone}
              onChange={handleChange}
              required
            >
              <option value="">Select tone</option>
              <option value="professional">Professional</option>
              <option value="conversational">Conversational</option>
              <option value="analytical">Analytical</option>
              <option value="opinionated">Opinionated</option>
              <option value="educational">Educational</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="default_host">Default Voice *</label>
            <select
              id="default_host"
              name="default_host"
              value={formData.default_host}
              onChange={handleChange}
              required
            >
              <option value="alloy">Alloy - Neutral, balanced voice</option>
              <option value="echo">Echo - Clear, professional voice</option>
              <option value="fable">Fable - Warm, storytelling voice</option>
              <option value="onyx">Onyx - Deep, authoritative voice</option>
              <option value="nova">Nova - Energetic, engaging voice</option>
              <option value="shimmer">Shimmer - Bright, optimistic voice</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="goals">Goals *</label>
            <textarea
              id="goals"
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              placeholder="What do you want to learn/achieve with this podcast?"
              rows={3}
              required
            />
          </div>

          <div className={styles.integrationsSection}>
            <h3>Integrations</h3>
            <p>Connect tools to enhance your podcast content</p>
            
            {integrations.length === 0 ? (
              <div className={styles.emptyIntegrations}>
                <button
                  type="button"
                  className={styles.addIntegrationButton}
                  onClick={handleAddIntegration}
                >
                  <span className={styles.plusIcon}>+</span>
                </button>
                <p>Add an integration to get started</p>
              </div>
            ) : (
              <div className={styles.integrationCards}>
                {integrations.map(integration => (
                  <div key={integration.id} className={styles.integrationCard}>
                    <div className={styles.integrationInfo}>
                      <div className={styles.integrationIcon}>
                        {integration.type === 'search_web' && <span className={styles.webIcon}>🌐</span>}
                      </div>
                      <div className={styles.integrationDetails}>
                        <h4>Search the Web</h4>
                        <p>Publications: {integration.settings.publications || 'Not configured'}</p>
                      </div>
                    </div>
                    <div className={styles.integrationActions}>
                      <button
                        type="button"
                        className={styles.editButton}
                        onClick={() => handleEditIntegration(integration)}
                        title="Edit integration"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveIntegration(integration.id)}
                        title="Remove integration"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Always show Add Integration button */}
                <button
                  type="button"
                  className={styles.addIntegrationButton}
                  onClick={handleAddIntegration}
                >
                  <span className={styles.plusIcon}>+</span>
                  Add Integration
                </button>
              </div>
            )}
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="create_initial_episode"
                checked={formData.create_initial_episode}
                onChange={handleChange}
                className={styles.checkbox}
              />
              <span className={styles.checkboxText}>Create Initial Episode Now</span>
            </label>
            <p className={styles.checkboxDescription}>
              Generate your first episode immediately after creating the podcast
            </p>
          </div>


          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={() => navigate(isEditMode ? `/podcast/get-podcast/${podcastId}` : '/dashboard')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update Podcast' : 'Create Podcast')
              }
            </button>
          </div>
        </form>
      </div>

      {/* Integration Selector Modal */}
      {showIntegrationSelector && (
        <div className={styles.modalOverlay}>
          <div className={styles.integrationSelectorModal}>
            <div className={styles.modalHeader}>
              <h3>Choose an Integration</h3>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => {
                  setShowIntegrationSelector(false);
                  document.body.style.overflow = 'unset';
                }}
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.integrationOptions}>
                <button
                  type="button"
                  className={styles.integrationOption}
                  onClick={() => handleSelectIntegration('search_web')}
                >
                  <span className={styles.optionIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4285F4"/>
                    </svg>
                  </span>
                  <div className={styles.optionDetails}>
                    <h4>Search the Web</h4>
                    <p>Search the web for relevant content and sources</p>
                  </div>
                </button>
                
                <button
                  type="button"
                  className={`${styles.integrationOption} ${styles.disabledOption}`}
                  disabled
                >
                  <span className={styles.optionIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="#4285F4"/>
                    </svg>
                  </span>
                  <div className={styles.optionDetails}>
                    <h4>Google Calendar</h4>
                    <p>Connect to your Google Calendar to get events and reminders for your podcast.</p>
                    <span className={styles.comingSoon}>Coming Soon</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  className={`${styles.integrationOption} ${styles.disabledOption}`}
                  disabled
                >
                  <span className={styles.optionIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#29B5E8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <div className={styles.optionDetails}>
                    <h4>Snowflake</h4>
                    <p>Access your Snowflake data to get insights and information for your data.</p>
                    <span className={styles.comingSoon}>Coming Soon</span>
                  </div>
                </button>
                
                <button
                  type="button"
                  className={`${styles.integrationOption} ${styles.disabledOption}`}
                  disabled
                >
                  <span className={styles.optionIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="#7B1FA2"/>
                    </svg>
                  </span>
                  <div className={styles.optionDetails}>
                    <h4>Yahoo Finance</h4>
                    <p>Get real-time stock market data and news for your podcast.</p>
                    <span className={styles.comingSoon}>Coming Soon</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration Settings Modal */}
      {showIntegrationModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.integrationModal}>
            <div className={styles.modalHeader}>
              <h3>Configure Search the Web</h3>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                setShowIntegrationModal(false);
                document.body.style.overflow = 'unset';
              }}
            >
              ×
            </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.formGroup}>
                <label htmlFor="publications">Publications/Sources</label>
                <textarea
                  id="publications"
                  name="publications"
                  value={integrationSettings.publications || ''}
                  onChange={handleIntegrationSettingsChange}
                  placeholder="e.g., The New York Times, BBC, TechCrunch, or specific websites you want to prioritize..."
                  rows={3}
                />
                <p className={styles.helpText}>
                  Specify the publications and sources you want the AI to search when creating your podcast content.
                </p>
              </div>
            </div>

            <div className={styles.modalActions}>
            <button
              type="button"
              onClick={() => {
                setShowIntegrationModal(false);
                setShowIntegrationSelector(true);
              }}
              className="btn btn-secondary"
            >
              Back
            </button>
              <button
                type="button"
                onClick={handleSaveIntegration}
                className="btn btn-primary"
              >
                Save Integration
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreatePodcastPage;
